import {SxProps, Theme} from '@mui/material';
import theme from 'src/theme';

/** The shared detail-page card. */
export const cardSx: SxProps<Theme> = {
    background: theme.palette.common.white,
    borderRadius: '14px',
    border: '1px solid rgba(0, 0, 0, 0.05)',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05), 0 4px 12px rgba(0,0,0,0.04)',
    overflow: 'hidden',
    // A column, so the facts grid below can take whatever height the row asks for and both cards in
    // the row end level. Written here rather than at the call site because the rail does the same.
    display: 'flex',
    flexDirection: 'column',
};

export const headerSx: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    gap: 2,
    flexWrap: 'wrap',
    px: 3,
    py: 4,
    background: `linear-gradient(180deg, ${theme.palette.primaryColor.A50} 0%, ${theme.palette.common.white} 100%)`,
};

/** Initials when there is no photograph — never a broken image and never an empty circle. */
export const avatarSx: SxProps<Theme> = {
    width: '58px',
    height: '58px',
    fontSize: '1.25rem',
    fontWeight: 600,
    background: `linear-gradient(135deg, ${theme.palette.primaryColor[50]} 0%, ${theme.palette.primaryColor[100]} 100%)`,
    color: theme.palette.primary.dark,
    flexShrink: 0,
};

export const titleSx: SxProps<Theme> = {
    fontSize: '1.625rem',
    fontWeight: 600,
    letterSpacing: '-0.015em',
};

export const subtitleSx: SxProps<Theme> = {
    fontSize: '0.75rem',
    color: theme.palette.neutral[500],
    mt: 0.25,
};

export const typePillSx: SxProps<Theme> = {
    display: 'inline-flex',
    alignItems: 'center',
    fontSize: '0.6875rem',
    fontWeight: 600,
    px: 1.25,
    py: 0.5,
    borderRadius: '999px',
    whiteSpace: 'nowrap',
    background: theme.palette.primaryColor.A50,
    color: theme.palette.primary.dark,
    border: `1px solid ${theme.palette.primaryColor.A100}`,
};

/**
 * The facts, two by two.
 *
 * Always four cells, whatever the user type has to put in them: a grid that loses a cell when a
 * value is missing re-flows the ones beside it, so the page looks different for two people of the
 * same kind. An absent value says so instead.
 */
export const factsSx: SxProps<Theme> = {
    flex: '1 1 auto',
    display: 'grid',
    gridTemplateColumns: {xs: '1fr', sm: 'repeat(2, 1fr)'},
    borderTop: `1px solid ${theme.palette.neutral[100]}`,
};

export const factSx = (index: number, total: number): SxProps<Theme> => ({
    px: 3,
    py: 3,
    borderRight: {xs: 'none', sm: index % 2 === 0 ? `1px solid ${theme.palette.neutral[100]}` : 'none'},
    borderBottom: index < total - 2 ? `1px solid ${theme.palette.neutral[100]}` : 'none',
});

export const factLabelSx: SxProps<Theme> = {
    fontSize: '0.625rem',
    fontWeight: 700,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: theme.palette.neutral[500],
};

export const factValueSx: SxProps<Theme> = {
    fontSize: '0.875rem',
    fontWeight: 500,
    mt: 0.4,
};

/**
 * A value that is not there.
 *
 * Lighter and in italics, so the eye reads it as "nothing here" rather than as content — and the row
 * keeps its height, which is what stops the card from changing shape person to person.
 */
export const factEmptySx: SxProps<Theme> = {
    fontSize: '0.875rem',
    fontWeight: 400,
    fontStyle: 'italic',
    color: theme.palette.neutral[400],
    mt: 0.4,
};

export const factNoteSx: SxProps<Theme> = {
    fontSize: '0.75rem',
    color: theme.palette.neutral[500],
    mt: 0.4,
};

/**
 * A settings strip under the facts.
 *
 * Same gutter and the same divider as a fact cell, so it reads as the card's last row rather than a
 * second card stuck underneath. Only drawn when a page passes one.
 */
export const footerSx: SxProps<Theme> = {
    px: 3,
    py: 2.5,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 0.5,
    borderTop: `1px solid ${theme.palette.neutral[100]}`,
};

/**
 * The same strip on a phone, where it cannot be the card's last row.
 *
 * The compact layout draws its own card, so a strip carrying only a top border would sit on the page
 * background with nothing under it. It gets the card's own ground instead.
 */
export const footerCompactSx: SxProps<Theme> = {
    mt: 1.5,
    px: 2,
    py: 2,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 0.5,
    background: theme.palette.common.white,
    borderRadius: '14px',
    border: '1px solid rgba(0, 0, 0, 0.05)',
};
